import React, { createContext, useContext, useCallback, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { useNotification } from './NotificationContext'
import { useSMBContext } from './SMBContext'
import {
  paymentsApi,
  BillingSummaryResponse,
  BillingPlan,
  ActiveSubscription,
  AIQuotaBucket,
  SMBData,
  BillingHistoryItem,
} from '../services/api'
import { usePaymentCheckout } from '../hooks/payments/usePaymentCheckout'
import { deriveSubscriptionState } from '../utils/paymentUtils'

export interface PaymentContextValue {
  smb: SMBData | null
  smbs: SMBData[]
  selectedSmbId: string
  setSmbId: (smbId: string) => void
  summary: BillingSummaryResponse | null
  plans: BillingPlan[]
  loading: boolean
  isError: boolean
  isRefreshing: boolean
  checkoutLoading: boolean
  pollingStatus: string | null
  activeSubscription: ActiveSubscription | null
  hasGrowthSubscription: boolean
  isEssentials: boolean
  hasAnySubscription: boolean
  hasAIAutomation: boolean
  hasPendingCheckout: boolean
  pendingTransaction: BillingHistoryItem | null
  quotaBuckets: AIQuotaBucket[]
  refreshBilling: () => Promise<void>
  handleSelectPlan: (planType: string) => Promise<void>
  handlePurchaseTopUp: (amount: number, units: number) => Promise<void>
  handleConfirmCancel: (subscriptionId?: string) => Promise<void>
}

const PaymentContext = createContext<PaymentContextValue | undefined>(undefined)

export const PaymentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const { showNotification } = useNotification()
  const { smbs, selectedSMB, selectedSMBId, setSelectedSMBId, loading: smbsLoading } = useSMBContext()
  const queryClient = useQueryClient()

  // TanStack Query 1: Billing Summary
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
  } = useQuery({
    queryKey: ['billingSummary', selectedSMBId],
    queryFn: () => paymentsApi.getBillingSummary(selectedSMBId),
    enabled: Boolean(isAuthenticated && selectedSMBId),
  })

  // TanStack Query 2: Billing Plans
  const {
    data: plansResponse,
    isLoading: isPlansLoading,
    isFetching: isPlansFetching,
    isError: isPlansError,
  } = useQuery({
    queryKey: ['billingPlans'],
    queryFn: () => paymentsApi.getBillingPlans(),
    enabled: Boolean(isAuthenticated),
  })

  const plans = plansResponse?.plans || []
  const currentSummary = summary || null

  // Derived loading state — bulletproof reactive loading!
  const loading = isAuthenticated
    ? smbsLoading || isPlansLoading || (smbs.length > 0 && (!selectedSMBId || isSummaryLoading))
    : false

  const isError = isAuthenticated ? isSummaryError || isPlansError : false

  const isRefreshing = isSummaryFetching || isPlansFetching

  const setSmbId = useCallback(
    (newSmbId: string) => {
      setSelectedSMBId(newSmbId)
    },
    [setSelectedSMBId]
  )

  const refreshBilling = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['billingSummary', selectedSMBId] }),
      queryClient.invalidateQueries({ queryKey: ['billingPlans'] }),
    ])
  }, [queryClient, selectedSMBId])

  const { checkoutLoading, pollingStatus, selectPlan, purchaseTopUp, cancelSubscription } =
    usePaymentCheckout({
      onSuccess: async () => {
        await refreshBilling()
      },
      showNotification,
    })

  const handleSelectPlan = useCallback(
    async (planType: string) => {
      if (selectedSMB?.id) await selectPlan(selectedSMB.id, planType)
    },
    [selectedSMB?.id, selectPlan]
  )

  const handlePurchaseTopUp = useCallback(
    async (amount: number, units: number) => {
      if (selectedSMB?.id) await purchaseTopUp(selectedSMB.id, amount, units)
    },
    [selectedSMB?.id, purchaseTopUp]
  )

  const handleConfirmCancel = useCallback(
    async (subscriptionId?: string) => {
      if (selectedSMB?.id) await cancelSubscription(selectedSMB.id, subscriptionId)
    },
    [selectedSMB?.id, cancelSubscription]
  )

  const derivedState = deriveSubscriptionState(currentSummary)

  const value: PaymentContextValue = React.useMemo(
    () => ({
      smb: selectedSMB,
      smbs,
      selectedSmbId: selectedSMBId,
      setSmbId,
      summary: currentSummary,
      plans,
      loading,
      isError,
      isRefreshing,
      checkoutLoading,
      pollingStatus,
      ...derivedState,
      refreshBilling,
      handleSelectPlan,
      handlePurchaseTopUp,
      handleConfirmCancel,
    }),
    [
      selectedSMB,
      smbs,
      selectedSMBId,
      setSmbId,
      currentSummary,
      plans,
      loading,
      isError,
      isRefreshing,
      checkoutLoading,
      pollingStatus,
      derivedState,
      refreshBilling,
      handleSelectPlan,
      handlePurchaseTopUp,
      handleConfirmCancel,
    ]
  )

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
}

export function usePaymentContext(): PaymentContextValue {
  const context = useContext(PaymentContext)
  if (!context) {
    throw new Error('usePaymentContext must be used within a PaymentProvider')
  }
  return context
}
